// src/pages/Dashboard.jsx
import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  ResponsiveContainer,
  ComposedChart,
  Area,
  Legend,
  Line,
} from "recharts";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  useGetDashboardSummaryQuery,
  useGetDashboardMonthlyTrendQuery,
} from "../toolkitServices/dashboardApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetQuotationsQuery } from "../toolkitServices/quotationApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetDebtCreditReportQuery } from "../toolkitServices/cashFlowApi";

/* ---------------- animations ---------------- */
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: "easeOut" },
  },
});

const modalOverlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};

const modalPanel = {
  hidden: { opacity: 0, y: 14, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.18 } },
  exit: { opacity: 0, y: 10, scale: 0.98, transition: { duration: 0.12 } },
};

/* ---------------- tiny icons (no extra lib) ---------------- */
const FunnelIcon = ({ className = "h-4 w-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 5h18l-7 8v6l-4 2v-8L3 5z" />
  </svg>
);

const SparkleIcon = ({ className = "h-4 w-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2l1.2 4.2L17.4 8 13.2 9.2 12 13.4 10.8 9.2 6.6 8l4.2-1.8L12 2z" />
    <path d="M19 13l.7 2.4L22 16l-2.3.6L19 19l-.7-2.4L16 16l2.3-.6L19 13z" />
    <path d="M5 14l.6 2.1L8 17l-2.4.9L5 20l-.6-2.1L2 17l2.4-.9L5 14z" />
  </svg>
);

const DownloadIcon = ({ className = "h-4 w-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v10" />
    <path d="M8 11l4 4 4-4" />
    <path d="M4 21h16" />
  </svg>
);

/* ---------------- helpers ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");
const toYmd = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/**
 * ✅ India-friendly money format (NO K/L/Cr)
 * Example: ₹ 12,34,567.00
 */
const shortInr = (v, decimals = 2) => {
  const n = Number(v || 0);
  if (!isFinite(n)) return "₹ 0";
  return `₹ ${n.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ---------------- UI atoms ---------------- */
const Card = ({ className = "", children }) => (
  <div
    className={[
      "rounded-3xl bg-white/90 ring-1 ring-slate-200/70 shadow-sm shadow-slate-200/60",
      "backdrop-blur supports-[backdrop-filter]:bg-white/80",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

const Sk = ({ className = "", style }) => (
  <div
    className={[
      "animate-pulse rounded-2xl bg-slate-200/70",
      "ring-1 ring-slate-200",
      className,
    ].join(" ")}
    style={style}
  />
);

const InlineSpinner = () => (
  <span className="inline-flex items-center gap-2">
    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.35)]" />
    <span className="text-[10px] rounded-full bg-slate-900/90 px-2 py-0.5 text-white">
      Syncing…
    </span>
  </span>
);

const TopProgress = ({ show }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        className="sticky top-0 z-[80] -mx-2 sm:-mx-4"
      >
        <div className="h-[3px] w-full overflow-hidden bg-slate-200/60">
          <div className="h-full w-[45%] animate-pulse bg-gradient-to-r from-violet-600 via-fuchsia-500 to-amber-500" />
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ---------------- chart palette (for dynamic categories) ---------------- */
const CAT_COLORS = [
  "#6366f1",
  "#22c55e",
  "#a855f7",
  "#f97316",
  "#06b6d4",
  "#e11d48",
  "#0ea5e9",
  "#84cc16",
  "#f59e0b",
  "#8b5cf6",
  "#14b8a6",
  "#ef4444",
];

export default function Dashboard() {
  const auth = useSelector((s) => s.auth);
  const user = auth?.user;

 

  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const displayName =
    user?.userName ||
    (user?.email ? user.email.split("@")[0] : "Owner / Admin");

  const storeName =
    user?.companyName || user?.clientCode || "Your jewellery brand";

  // ✅ presets + custom range
  const [rangeKey, setRangeKey] = useState("TODAY"); // TODAY | 7D | 30D | CUSTOM

  const todayYmd = toYmd(new Date());
  const [customRange, setCustomRange] = useState({
    from: todayYmd,
    to: todayYmd,
  });
  const [draftRange, setDraftRange] = useState({
    from: todayYmd,
    to: todayYmd,
  });
  const [rangeError, setRangeError] = useState("");

  // ✅ Branch filter
  const { data: branchesRes, isLoading: branchesLoading } =
    useGetAllBranchesQuery(
      user ? { clientCode: user?.clientCode } : skipToken,
      { refetchOnMountOrArgChange: true }
    );

  const branches = useMemo(() => {
    const raw = branchesRes?.data || branchesRes || [];
    return Array.isArray(raw) ? raw : [];
  }, [branchesRes]);

  const [selectedBranchId, setSelectedBranchId] = useState("ALL");

 
  // ✅ Filter modal open state
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { fromDate, toDate } = useMemo(() => {
    if (rangeKey === "CUSTOM")
      return { fromDate: customRange.from, toDate: customRange.to };

    const end = new Date();
    const start = new Date();

    if (rangeKey === "7D") start.setDate(start.getDate() - 6);
    else if (rangeKey === "30D") start.setDate(start.getDate() - 29);

    return { fromDate: toYmd(start), toDate: toYmd(end) };
  }, [rangeKey, customRange]);

  // ✅ IMPORTANT: ALL -> send "" (backend should treat as consolidated)
  const effectiveBranchId = selectedBranchId === "ALL" ? "" : selectedBranchId;

  const queryArgs = user
    ? {
        branchId: effectiveBranchId,
        fromDate,
        toDate,
      }
    : skipToken;

      const { data: qtnRes } = useGetQuotationsQuery(
  user
    ? {
        page: 1,
        limit: 10, // thoda zyada lo, filter client side
        branchId: effectiveBranchId,
      }
    : skipToken
);
  const { data: outstandingRes, isFetching: outstandingFetching } =
    useGetDebtCreditReportQuery(
      user
        ? {
            branchId: effectiveBranchId,
          }
        : skipToken
    );

  const {
    data: dashRes,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetDashboardSummaryQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  // normalize response
  const dashboard = useMemo(() => {
    if (!dashRes) return {};
    if (dashRes?.data && (dashRes.data.today || dashRes.data.range))
      return dashRes.data;
    return dashRes;
  }, [dashRes]);

  const pendingFollowUps = useMemo(() => {
  const rows = qtnRes?.rows || qtnRes?.data?.rows || [];

  return rows
    .filter(
      (q) =>
        q?.followUp?.enabled === true &&
        q?.status !== "Expired"
    )
    .slice(0, 5); // 🔒 max 5
}, [qtnRes]);

  const topOutstanding = useMemo(() => {
    const entries = outstandingRes?.entries || outstandingRes?.data?.entries || [];
    const rows = Array.isArray(entries) ? entries : [];

    return rows
      .filter((inv) => Number(inv?.totals?.outstanding || inv?.outstanding || 0) > 0)
      .sort(
        (a, b) =>
          Number(b?.totals?.outstanding || b?.outstanding || 0) -
          Number(a?.totals?.outstanding || a?.outstanding || 0)
      )
      .slice(0, 5);
  }, [outstandingRes]);

  const totalOutstanding = Number(
    outstandingRes?.totalOutstanding || outstandingRes?.data?.totalOutstanding || 0
  );

  const apiToday = dashboard?.today || {};
  const apiRange = dashboard?.range || {};

  // loading modes
  const isInitialLoading = isLoading && !dashRes; // first paint
  const isBusy = isFetching || isLoading; // includes background refresh

  const schemeCollections = useMemo(
    () => [
      { day: "Mon", amount: 34000 },
      { day: "Tue", amount: 28000 },
      { day: "Wed", amount: 39000 },
      { day: "Thu", amount: 45000 },
      { day: "Fri", amount: 31000 },
      { day: "Sat", amount: 52000 },
      { day: "Sun", amount: 27000 },
    ],
    []
  );

  const todayActivity = useMemo(() => {
    const g = Number(apiToday?.grossSales || 0);
    const r = Number(apiToday?.salesReturn || 0);
    const n = Number(apiToday?.netSales || 0);
    const inv = Number(apiToday?.invoiceCount || 0);
    const ret = Number(apiToday?.returnCount || 0);

    if (!g && !r && !n && !inv && !ret) {
      return [
        "Invoice #ND-1204 • ₹ 1.72 L • New diamond ring set",
        "Old gold taken: 42.250 g • Adjusted against bill ND-1205",
        "Scheme instalment received from 7 customers",
        "New customer added from Instagram campaign",
      ];
    }

    return [
      `Gross Sales: ${shortInr(g)} • Invoices: ${inv}`,
      `Sales Return: ${shortInr(r)} • Returns: ${ret}`,
      `Net Sales: ${shortInr(n)} • Avg Bill: ${shortInr(apiToday?.avgBill || 0)}`,
    ];
  }, [apiToday]);

  const alerts = useMemo(() => {
    const arr = [];
    if (Number(apiToday?.returnCount || 0) > 0)
      arr.push(
        `Returns today: ${apiToday.returnCount} (check reason / stock back entries)`
      );
    if (
      Number(apiToday?.netSales || 0) === 0 &&
      Number(apiToday?.invoiceCount || 0) > 0
    )
      arr.push("Net sales is 0 but invoices exist (returns equal sales?)");
    if (!arr.length) {
      return [
        "3 designs in 22K ring category reached low stock level",
        "2 gold schemes maturing in next 7 days",
        "Customer Ankit Shah has pending follow-up for solitaire inquiry",
      ];
    }
    return arr.slice(0, 3);
  }, [apiToday]);

  const quickShortcuts = [
    {
      title: "Create Invoice",
      desc: "Start new retail / wholesale bill",
      to: "/sales/sales-list/create-sale",
      tag: "Billing",
      emoji: "🧾",
    },
    {
      title: "Manage Stock",
      desc: "Labelled",
      to: "/stocks/labelled-stock-list/create-labelled-stock",
      tag: "Stock",
      emoji: "📦",
    },
    {
      title: "Manage Stock",
      desc: "Un-Labelled",
      to: "/stocks/unlabelled-stock-list/create-unlabelled-stock",
      tag: "Stock",
      emoji: "📦",
    },
    {
      title: "Gold Schemes",
      desc: "Enrolments & instalments",
      to: "/scheme/list",
      tag: "Schemes",
      emoji: "🏅",
    },
    {
      title: "Customer Ledger",
      desc: "Receivables & history",
      to: "/transactions/customer",
      tag: "Accounts",
      emoji: "👤",
    },
  ];

  const rangeLabel =
    apiRange?.start && apiRange?.end
      ? `${fmtDateTime(apiRange.start)} → ${fmtDateTime(apiRange.end)}`
      : `${fromDate} → ${toDate}`;

  const rangeShortLabel = useMemo(() => {
    if (rangeKey === "TODAY") return "Today";
    if (rangeKey === "7D") return "Last 7 days";
    if (rangeKey === "30D") return "Last 30 days";
    return `${customRange.from} → ${customRange.to}`;
  }, [rangeKey, customRange]);

  // ✅ custom apply handlers
  const applyCustomRange = () => {
    setRangeError("");

    const f = (draftRange.from || "").trim();
    const t = (draftRange.to || "").trim();

    if (!f || !t) {
      setRangeError("Please select both From and To dates.");
      return;
    }

    const from = f <= t ? f : t;
    const to = f <= t ? t : f;

    setCustomRange({ from, to });
    setRangeKey("CUSTOM");
  };

  const resetToToday = () => {
    const t = toYmd(new Date());
    setRangeError("");
    setCustomRange({ from: t, to: t });
    setDraftRange({ from: t, to: t });
    setRangeKey("TODAY");
  };

  const setPreset = (key) => {
    setRangeError("");
    setRangeKey(key);
    if (key === "CUSTOM") setDraftRange(customRange);
  };

  const currentYear = new Date().getFullYear();

  const selectedBranchLabel = useMemo(() => {
    if (selectedBranchId === "ALL") return "All branches (Consolidated)";
    const b = branches.find((x) => (x?._id || x?.id) === selectedBranchId);
    return b?.BranchName || b?.branchName || b?.name || "Selected branch";
  }, [selectedBranchId, branches]);

  const activeFilterCount = useMemo(() => {
    const branchOn = selectedBranchId !== "ALL";
    const customOn = rangeKey === "CUSTOM";
    return Number(branchOn) + Number(customOn);
  }, [selectedBranchId, rangeKey]);

  const closeFilter = () => setIsFilterOpen(false);

  // ✅ UX: ESC closes + body scroll lock
  useEffect(() => {
    if (!isFilterOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") closeFilter();
    };

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isFilterOpen]);

  /* =========================
     MONTHLY TREND (premium)
  ========================== */

  // ✅ categories
  const { data: catRes } = useGetAllCategoriesQuery(
    user
      ? { clientCode: user?.clientCode, page: 1, limit: 1000, active: true }
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const categories = useMemo(() => {
    const raw = catRes?.data || catRes || [];
    const arr = Array.isArray(raw) ? raw : [];
    return arr.map((c) => ({
      value: c._id || c.id,
      label: c.categoryName || c.name,
    }));
  }, [catRes]);

  // Monthly chart category (existing)
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  // ✅ Product chart category (NEW) - default first category
  const [productCategoryId, setProductCategoryId] = useState("");
  const [designCategoryId, setDesignCategoryId] = useState("");
const [designProductId, setDesignProductId] = useState("");

  useEffect(() => {
    if (!productCategoryId && categories.length) {
      setProductCategoryId(categories[0].value); // ✅ default first category
    }
  }, [categories, productCategoryId]);

  useEffect(() => {
  if (!designCategoryId && categories.length) {
    setDesignCategoryId(categories[0].value);
  }
}, [categories, designCategoryId]);
const { data: designProdRes, isLoading: designProdLoading } =
  useGetAllProductsQuery(
    user && designCategoryId
      ? { page: 1, limit: 1000, search: "", active: true, category: designCategoryId }
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );


  const designProducts = useMemo(() => {
  const raw = designProdRes?.data || designProdRes || [];
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((p) => ({
    value: p._id || p.id,
    label: p.productName || p.name || "Product",
  }));
}, [designProdRes]);

useEffect(() => {
  if (!designProducts.length) {
    setDesignProductId("");
    return;
  }
  // if not selected or selected not found => set first
  const exists = designProducts.some((x) => x.value === designProductId);
  if (!designProductId || !exists) {
    setDesignProductId(designProducts[0].value);
  }
}, [designProducts, designProductId]);

const designCategoryLabel = useMemo(() => {
  const c = categories.find((x) => x.value === designCategoryId);
  return c?.label || "";
}, [categories, designCategoryId]);

const designProductLabel = useMemo(() => {
  const p = designProducts.find((x) => x.value === designProductId);
  return p?.label || "";
}, [designProducts, designProductId]);

// query to get designTotals
const designArgs =
  user && fromDate && toDate && designCategoryId && designProductId
    ? {
        branchId: effectiveBranchId,
        fromDate,
        toDate,
        categoryId: designCategoryId,
        category: designCategoryLabel,      // ✅ name send to avoid id mismatch
        productId: designProductId,
        product: designProductLabel,        // ✅ name send
        includeCategoryBreakdown: "0",
        productBreakdown: "0",
        designBreakdown: "1",
        designTop: "12",
      }
    : skipToken;

const { data: designRes, isLoading: designLoading } =
  useGetDashboardMonthlyTrendQuery(designArgs, {
    refetchOnMountOrArgChange: true,
  });

const designDataRaw = useMemo(() => {
  const res = designRes?.data ? designRes.data : designRes;
  const arr = res?.designTotals || [];
  return Array.isArray(arr) ? arr : [];
}, [designRes]);

const designMix = useMemo(() => {
  return designDataRaw.map((x) => ({
    name: x.designName || "Unknown",
    amount: Number(x.amount || 0),
  }));
}, [designDataRaw]);

const designChartTitle = useMemo(() => {
  const c = categories.find((x) => x.value === designCategoryId);
  const p = designProducts.find((x) => x.value === designProductId);
  const cName = c?.label || "Category";
  const pName = p?.label || "Product";
  return `${cName} • ${pName}`;
}, [categories, designProducts, designCategoryId, designProductId]);

  const yearOptions = useMemo(() => {
    return Array.from({ length: 5 }).map((_, i) => {
      const y = currentYear - i;
      return { value: y, label: String(y) };
    });
  }, [currentYear]);

  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { trendFromDate, trendToDate } = useMemo(() => {
    if (!selectedYear) return { trendFromDate: "", trendToDate: "" };

    const isCurrentYear = selectedYear === currentYear;

    const from = `${selectedYear}-01-01`;
    const to = isCurrentYear ? toYmd(new Date()) : `${selectedYear}-12-31`;

    return { trendFromDate: from, trendToDate: to };
  }, [selectedYear, currentYear]);

  const monthlyArgs =
    user && trendFromDate && trendToDate
      ? {
          branchId: effectiveBranchId,
          fromDate: trendFromDate,
          toDate: trendToDate,
          categoryId: selectedCategoryId || "",
          includeCategoryBreakdown: selectedCategoryId ? "0" : "1",
        }
      : skipToken;

  const { data: monthlyRes, isLoading: monthlyLoading } =
    useGetDashboardMonthlyTrendQuery(monthlyArgs, {
      refetchOnMountOrArgChange: true,
    });

  // normalize monthly response
  const monthly = useMemo(() => {
    if (!monthlyRes) return {};
    if (monthlyRes?.data) return monthlyRes.data;
    return monthlyRes;
  }, [monthlyRes]);

  /* ---- Trend mapping (gross/return/net + counts) ---- */
  const salesTrend = useMemo(() => {
    const arr = monthly?.trend || [];
    const safe = Array.isArray(arr) ? arr : [];

    return safe.map((m) => {
      const gold = Number(m.gold || 0);
      const diamond = Number(m.diamond || 0);

      const total = Number(m.total ?? gold + diamond);
      const grossTotal = Number(m.grossTotal ?? total);
      const returnTotal = Number(m.returnTotal || 0);
      const netTotal = Number(m.netTotal ?? grossTotal - returnTotal);

      return {
        ym: m.ym,
        month: m.month,
        gold,
        diamond,
        total,
        grossTotal,
        returnTotal,
        netTotal,
        invoiceCount: Number(m.invoiceCount || 0),
        returnCount: Number(m.returnCount || 0),
      };
    });
  }, [monthly]);

  const trendData = useMemo(() => {
    const arr = Array.isArray(salesTrend) ? salesTrend : [];
    return arr;
  }, [salesTrend]);

  const lastPoint = trendData?.[trendData.length - 1] || {};
  const prevPoint = trendData?.[trendData.length - 2] || {};

  const pct = (now, prev) => {
    const n = Number(now || 0);
    const p = Number(prev || 0);
    if (!p) return "—";
    const v = ((n - p) / p) * 100;
    const sign = v > 0 ? "+" : "";
    return `${sign}${v.toFixed(1)}%`;
  };

  /* ---- Category monthly breakdown helpers ---- */
  const categoryMonthly = useMemo(() => {
    const arr = monthly?.categoryMonthly || [];
    return Array.isArray(arr) ? arr : [];
  }, [monthly]);

  const categoryNames = useMemo(() => {
    const seen = new Set();
    const names = [];
    for (const r of categoryMonthly) {
      const name = (r?.categoryName || "Unknown").trim();
      if (!seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    }
    return names;
  }, [categoryMonthly]);

  const categoryByMonth = useMemo(() => {
    const map = new Map();
    for (const r of categoryMonthly) {
      const key = r?.month || r?.ym;
      if (!key) continue;

      if (!map.has(key)) map.set(key, {});
      const obj = map.get(key);

      const name = (r?.categoryName || "Unknown").trim();
      obj[name] = Number(r?.total ?? 0);
    }
    return map;
  }, [categoryMonthly]);

  const hasCategoryBreakdown = Boolean(!selectedCategoryId && categoryNames.length);

  const chartData = useMemo(() => {
    const rows = trendData || [];
    return rows.map((r) => {
      const monthLabel = r?.month ?? "";
      const cats = categoryByMonth.get(monthLabel) || {};

      const base = {
        month: monthLabel,
        grossTotal: Number(r?.grossTotal || 0),
        returnTotal: Number(r?.returnTotal || 0),
        netTotal: Number(r?.netTotal || 0),
        total: Number(r?.total || 0),
        invoiceCount: Number(r?.invoiceCount || 0),
        returnCount: Number(r?.returnCount || 0),
        gold: Number(r?.gold || 0),
        diamond: Number(r?.diamond || 0),
      };

      if (!hasCategoryBreakdown) return base;

      const obj = { ...base };
      for (const nm of categoryNames) {
        obj[nm] = Number(cats?.[nm] || 0);
      }
      return obj;
    });
  }, [trendData, categoryByMonth, hasCategoryBreakdown, categoryNames]);

  /* ---- Insights (rank by netTotal) ---- */
  const trendInsights = useMemo(() => {
    const arr = [...(trendData || [])];
    arr.sort((a, b) => Number(b?.netTotal || 0) - Number(a?.netTotal || 0));
    const top3 = arr.slice(0, 3);
    return { best: top3[0] || null, top3 };
  }, [trendData]);

  const bestMonthTopCats = useMemo(() => {
    if (!trendInsights?.best?.month) return [];
    const cats = categoryByMonth.get(trendInsights.best.month) || {};
    const list = Object.entries(cats).map(([name, value]) => ({
      name,
      value: Number(value || 0),
    }));
    list.sort((a, b) => b.value - a.value);
    return list.slice(0, 4);
  }, [trendInsights, categoryByMonth]);

  /* ---- CSV download (dynamic categories, no gold/diamond) ---- */
  const downloadTrendCsv = () => {
    const rows = trendData || [];
    if (!rows.length) return;

    const esc = (v) => {
      const s = String(v ?? "");
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    if (categoryNames.length) {
      const header = ["month", ...categoryNames, "grossTotal", "returnTotal", "netTotal"];
      const lines = [
        header.map(esc).join(","),
        ...rows.map((r) => {
          const monthLabel = r?.month ?? "";
          const cats = categoryByMonth.get(monthLabel) || {};
          const catVals = categoryNames.map((nm) => Number(cats?.[nm] || 0));

          const grossTotal = Number(r?.grossTotal ?? r?.total ?? 0);
          const returnTotal = Number(r?.returnTotal || 0);
          const netTotal = Number(r?.netTotal ?? (grossTotal - returnTotal));

          return [esc(monthLabel), ...catVals, grossTotal, returnTotal, netTotal].join(",");
        }),
      ];

      const csv = lines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `monthly_sales_category_${toYmd(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return;
    }

    const header = ["month", "grossTotal", "returnTotal", "netTotal"];
    const lines = [
      header.join(","),
      ...rows.map((r) => {
        const grossTotal = Number(r?.grossTotal ?? r?.total ?? 0);
        const returnTotal = Number(r?.returnTotal || 0);
        const netTotal = Number(r?.netTotal ?? (grossTotal - returnTotal));
        return [esc(r?.month ?? ""), grossTotal, returnTotal, netTotal].join(",");
      }),
    ];

    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `monthly_sales_trend_${toYmd(new Date())}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ---- Tooltip (category wise + gross/return/net) ---- */
  const TrendTooltip = ({ active, label }) => {
    if (!active) return null;

    const row = chartData.find((x) => x.month === label) || {};
    const gross = Number(row?.grossTotal || 0);
    const ret = Number(row?.returnTotal || 0);
    const net = Number(row?.netTotal || 0);

    if (hasCategoryBreakdown && categoryNames.length) {
      const list = categoryNames
        .map((nm) => ({ name: nm, value: Number(row?.[nm] || 0) }))
        .sort((a, b) => b.value - a.value);

      const top = list.slice(0, 6);
      const more = Math.max(0, list.length - top.length);

      return (
        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-xl">
          <div className="text-[11px] font-semibold text-slate-800">{label}</div>

          <div className="mt-1 space-y-1 text-[11px] text-slate-600">
            {top.map((x) => (
              <div key={x.name} className="flex items-center justify-between gap-6">
                <span className="truncate max-w-[130px]">{x.name}</span>
                <span className="font-semibold text-slate-900">
                  ₹ {x.value.toLocaleString("en-IN")}
                </span>
              </div>
            ))}

            {more > 0 && (
              <div className="text-[10px] text-slate-400">+ {more} more categories</div>
            )}

            <div className="my-1 h-px bg-slate-100" />

            <div className="flex items-center justify-between gap-6">
              <span>Gross</span>
              <span className="font-semibold text-slate-900">
                ₹ {gross.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span>Returns</span>
              <span className="font-semibold text-amber-700">
                ₹ {ret.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="font-semibold">Net</span>
              <span className="font-extrabold text-violet-700">
                ₹ {net.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>
      );
    }

    const gold = Number(row?.gold || 0);
    const diamond = Number(row?.diamond || 0);
    const total = Number(row?.total ?? gold + diamond);

    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-xl">
        <div className="text-[11px] font-semibold text-slate-800">{label}</div>
        <div className="mt-1 space-y-1 text-[11px] text-slate-600">
          <div className="flex items-center justify-between gap-6">
            <span>Gold</span>
            <span className="font-semibold text-slate-900">
              ₹ {gold.toLocaleString("en-IN")}
            </span>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span>Diamond</span>
            <span className="font-semibold text-slate-900">
              ₹ {diamond.toLocaleString("en-IN")}
            </span>
          </div>
          <div className="my-1 h-px bg-slate-100" />
          <div className="flex items-center justify-between gap-6">
            <span>Total</span>
            <span className="font-extrabold text-violet-700">
              ₹ {total.toLocaleString("en-IN")}
            </span>
          </div>
          <div className="my-1 h-px bg-slate-100" />
          <div className="flex items-center justify-between gap-6">
            <span>Net</span>
            <span className="font-semibold text-slate-900">
              ₹ {net.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const trendBusy = isInitialLoading || monthlyLoading;

  /* =========================
     ✅ PRODUCT-WISE SALES CHART (NEW)
     Uses same monthly-trend endpoint with:
       - fromDate/toDate = dashboard range
       - categoryId = selected category
       - productBreakdown=1
  ========================== */

  const productArgs =
    user && fromDate && toDate && productCategoryId
      ? {
          branchId: effectiveBranchId,
          fromDate,
          toDate,
          categoryId: productCategoryId,
          includeCategoryBreakdown: "0",
          productBreakdown: "1",
          productTop: "12",
        }
      : skipToken;

  const { data: productRes, isLoading: productLoading } =
    useGetDashboardMonthlyTrendQuery(productArgs, {
      refetchOnMountOrArgChange: true,
    });

  const productDataRaw = useMemo(() => {
    const res = productRes?.data ? productRes.data : productRes;
    const arr = res?.productTotals || [];
    return Array.isArray(arr) ? arr : [];
  }, [productRes]);

  const productMix = useMemo(() => {
    return productDataRaw.map((x) => ({
      name: x.productName || "Unknown",
      amount: Number(x.amount || 0),
    }));
  }, [productDataRaw]);

  const productChartTitle = useMemo(() => {
    const c = categories.find((x) => x.value === productCategoryId);
    return c?.label || "Category";
  }, [categories, productCategoryId]);

  /* ========================= */

  // ----- skeleton blocks -----
  const ShortcutsSkeleton = () => (
    <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl bg-white/90 p-3 ring-1 ring-slate-200/70 shadow-sm shadow-slate-200/60"
        >
          <div className="flex items-start gap-3">
            <Sk className="h-9 w-9 rounded-2xl" />
            <div className="flex-1 space-y-2">
              <Sk className="h-3 w-16 rounded-lg" />
              <Sk className="h-4 w-24 rounded-lg" />
              <Sk className="h-3 w-32 rounded-lg" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <Sk className="h-3 w-20 rounded-lg" />
            <Sk className="h-3 w-10 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );

  const KpiSkeleton = () => (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="relative overflow-hidden rounded-2xl bg-white/95 p-4 ring-1 ring-slate-200/70 shadow-sm shadow-slate-200/60"
        >
          <div className="space-y-2">
            <Sk className="h-3 w-24 rounded-lg" />
            <Sk className="h-8 w-32 rounded-xl" />
            <Sk className="h-3 w-28 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );

  const ChartSkeleton = ({ h = 280 }) => (
    <div className="rounded-2xl bg-gradient-to-b from-slate-50 to-white px-3 py-3 ring-1 ring-slate-200">
      <div className="flex items-center justify-between">
        <Sk className="h-4 w-40 rounded-lg" />
        <Sk className="h-8 w-28 rounded-full" />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-2xl bg-white p-3 ring-1 ring-slate-200">
            <Sk className="h-3 w-24 rounded-lg" />
            <Sk className="mt-2 h-4 w-20 rounded-lg" />
            <Sk className="mt-2 h-3 w-16 rounded-lg" />
          </div>
        ))}
      </div>
      <Sk className="mt-3 w-full rounded-2xl" style={{ height: h }} />
    </div>
  );

  const ListSkeleton = ({ rows = 4, tone = "slate" }) => (
    <ul className="mt-3 space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          className={[
            "flex items-start gap-2 rounded-2xl px-3 py-2 ring-1",
            tone === "amber"
              ? "bg-amber-50/70 ring-amber-100"
              : "bg-slate-50/70 ring-slate-200",
          ].join(" ")}
        >
          <Sk className="mt-[6px] h-2 w-2 rounded-full" />
          <div className="flex-1 space-y-2">
            <Sk className="h-3 w-[85%] rounded-lg" />
            <Sk className="h-3 w-[60%] rounded-lg" />
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="h-[88vh] overflow-y-auto overflow-x-hidden bg-gradient-to-br from-[#f6f7ff] via-[#fffdf2] to-[#eef7ff] px-2 pb-2 sm:px-4">
   

     
      {/* subtle top progress when fetching */}
      <TopProgress show={isFetching && !isInitialLoading} />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeUp(0)}
        className="mx-auto max-w-7xl text-slate-900 pb-3"
      >
        {/* HEADER ROW */}
        <div className="mb-4 space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2 min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/85 px-3 py-1 text-[11px] font-medium text-violet-700 ring-1 ring-violet-200 shadow-sm shadow-violet-100">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.35)]" />
                <span className="truncate">Live jewellery retail dashboard</span>

                {isBusy && <InlineSpinner />}

                {isError && (
                  <button
                    onClick={refetch}
                    className="ml-1 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] text-white"
                    title={String(
                      error?.data?.message || error?.error || "Failed"
                    )}
                    type="button"
                  >
                    Retry
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white/80 ring-1 ring-slate-200 shadow-sm">
                  <span className="text-lg">💎</span>
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl md:text-2xl">
                    Hello,{" "}
                    <span className="bg-gradient-to-r from-violet-700 via-fuchsia-500 to-amber-500 bg-clip-text text-transparent">
                      {displayName}
                    </span>
                  </h1>
                  <p className="text-xs text-slate-600 sm:text-sm">
                    Quick view of billing, returns & insights for your selected
                    range.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CHIPS + ACTIONS ROW */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {/* chips */}
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500 min-w-0">
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-slate-100 px-2.5 py-1 text-[10px] shadow-md shadow-slate-900/30">
                <span className="text-[13px]">🏬</span>
                <span className="font-medium truncate max-w-[220px]">
                  {storeName}
                </span>
              </span>

              <span className="inline-flex items-center gap-1 rounded-full bg-white/75 px-2.5 py-1 ring-1 ring-slate-200 shadow-sm">
                <span className="text-[13px]">📅</span>
                <span>{formattedDate}</span>
              </span>

              <span className="inline-flex items-center gap-1 rounded-full bg-white/75 px-2.5 py-1 ring-1 ring-slate-200 shadow-sm max-w-full min-w-0">
                <span className="text-[13px] shrink-0">🗓️</span>
                <span className="font-medium truncate min-w-0">{rangeLabel}</span>
              </span>

              <span className="inline-flex items-center gap-1 rounded-full bg-white/75 px-2.5 py-1 ring-1 ring-slate-200 shadow-sm max-w-full min-w-0">
                <span className="text-[13px] shrink-0">🏷️</span>
                <span className="font-medium truncate min-w-0">
                  {selectedBranchLabel}
                </span>
              </span>
            </div>

            {/* actions */}
            <div className="flex items-center justify-end gap-2">
              {/* premium filter button */}
              <button
                onClick={() => {
                  setRangeError("");
                  setDraftRange(customRange);
                  setIsFilterOpen(true);
                }}
                className="group relative overflow-hidden rounded-2xl shadow-lg shadow-violet-300/35"
                title="Filters (Branch + Date range)"
                type="button"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-amber-500 opacity-90 transition-opacity group-hover:opacity-100" />
                <span className="relative inline-flex items-center gap-3 rounded-2xl bg-slate-950/90 px-3 py-2 text-white ring-1 ring-white/10">
                  <span className="grid h-8 w-8 place-items-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                    <FunnelIcon className="h-[14px] w-[14px] text-white" />
                  </span>

                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-[10px] text-white/70">
                      {rangeShortLabel}
                      {selectedBranchId !== "ALL" ? " • Branch" : " • All"}
                    </span>
                    <span className="text-[11px] font-semibold">Filters</span>
                  </span>

                  {activeFilterCount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[10px] font-semibold ring-1 ring-white/15">
                      <SparkleIcon className="h-3.5 w-3.5 text-white/90" />
                      <span>{activeFilterCount}</span>
                    </span>
                  )}
                </span>
              </button>

              <button
                onClick={resetToToday}
                className="rounded-2xl border border-slate-200/80 bg-white/75 px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-white hover:border-violet-200 hover:text-violet-700"
                title="Reset to Today"
                type="button"
              >
                Reset
              </button>

              {isError && (
                <button
                  onClick={refetch}
                  className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700 shadow-sm hover:bg-rose-100"
                  type="button"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ✅ FILTER MODAL */}
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              variants={modalOverlay}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-0 z-[999] flex items-end justify-center bg-black/45 sm:items-center sm:p-4"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeFilter();
              }}
              role="dialog"
              aria-modal="true"
            >
              <div className="w-full px-3 pb-[max(12px,env(safe-area-inset-bottom))] sm:px-0 sm:pb-0 flex justify-center">
                <motion.div
                  variants={modalPanel}
                  className="w-full max-w-[560px] overflow-hidden bg-white ring-1 ring-slate-200 shadow-2xl shadow-slate-900/30 rounded-t-3xl rounded-b-none sm:rounded-3xl max-h-[85vh] sm:max-h-[80vh] flex flex-col"
                >
                  <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-gradient-to-br from-violet-50 via-white to-amber-50 px-4 py-3 backdrop-blur">
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-slate-900">
                        Filters
                      </p>
                      <p className="text-[10px] text-slate-500">
                        Branch + Date range •{" "}
                        <span className="font-semibold">ESC</span> to close
                      </p>
                    </div>

                    <button
                      onClick={closeFilter}
                      className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-slate-300"
                      type="button"
                    >
                      ✕ Close
                    </button>
                  </div>

                  <div className="px-4 py-4 overflow-y-auto">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1 text-[10px] font-semibold text-white">
                        <FunnelIcon className="h-3.5 w-3.5 text-white" />
                        Applied
                      </span>
                      <span className="rounded-full bg-slate-50 px-3 py-1 text-[10px] font-semibold text-slate-700 ring-1 ring-slate-200">
                        {selectedBranchLabel}
                      </span>
                      <span className="rounded-full bg-slate-50 px-3 py-1 text-[10px] font-semibold text-slate-700 ring-1 ring-slate-200">
                        {rangeLabel}
                      </span>
                    </div>

                    {/* Branch */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-semibold text-slate-800">
                          Branch
                        </p>
                        {branchesLoading && (
                          <span className="text-[10px] text-slate-400">
                            Loading…
                          </span>
                        )}
                      </div>

                      <div className="mt-2">
                        <select
                          value={selectedBranchId}
                          onChange={(e) => setSelectedBranchId(e.target.value)}
                          className="h-11 sm:h-10 w-full rounded-2xl border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                        >
                          <option value="ALL">
                            All branches (Consolidated)
                          </option>
                          {branches.map((b) => {
                            const id = b?._id || b?.id;
                            const name =
                              b?.BranchName ||
                              b?.branchName ||
                              b?.name ||
                              "Branch";
                            return (
                              <option key={id} value={id}>
                                {name}
                              </option>
                            );
                          })}
                        </select>

                        <p className="mt-1 text-[10px] text-slate-500">
                          All = consolidated totals.
                        </p>
                      </div>
                    </div>

                    {/* Date range */}
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3">
                      <p className="text-[11px] font-semibold text-slate-800">
                        Date range
                      </p>

                      <div className="mt-2 flex flex-wrap gap-2 sm:gap-2">
                        {[
                          ["TODAY", "Today"],
                          ["7D", "Last 7 days"],
                          ["30D", "Last 30 days"],
                          ["CUSTOM", "Custom"],
                        ].map(([k, t]) => (
                          <button
                            key={k}
                            onClick={() => setPreset(k)}
                            className={`rounded-full px-3 py-1 text-[11px] font-semibold shadow-sm transition-all ${
                              rangeKey === k
                                ? "bg-violet-600 text-white"
                                : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                            }`}
                            type="button"
                          >
                            {t}
                          </button>
                        ))}
                      </div>

                      {rangeKey === "CUSTOM" && (
                        <div className="mt-3">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <label className="flex flex-col gap-1">
                              <span className="text-[10px] font-medium text-slate-500">
                                From
                              </span>
                              <input
                                type="date"
                                value={draftRange.from}
                                onChange={(e) => {
                                  setRangeError("");
                                  setDraftRange((p) => ({
                                    ...p,
                                    from: e.target.value,
                                  }));
                                }}
                                className="h-11 sm:h-10 w-full rounded-2xl border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                              />
                            </label>

                            <label className="flex flex-col gap-1">
                              <span className="text-[10px] font-medium text-slate-500">
                                To
                              </span>
                              <input
                                type="date"
                                value={draftRange.to}
                                onChange={(e) => {
                                  setRangeError("");
                                  setDraftRange((p) => ({
                                    ...p,
                                    to: e.target.value,
                                  }));
                                }}
                                className="h-11 sm:h-10 w-full rounded-2xl border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                              />
                            </label>
                          </div>

                          {rangeError && (
                            <div className="mt-2 rounded-2xl bg-rose-50 px-3 py-2 text-[11px] text-rose-700 ring-1 ring-rose-200">
                              {rangeError}
                            </div>
                          )}

                          <div className="mt-2 flex items-center justify-between gap-2">
                            <button
                              onClick={() => {
                                const t = toYmd(new Date());
                                setRangeError("");
                                setDraftRange({ from: t, to: t });
                              }}
                              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 hover:border-violet-300"
                              type="button"
                            >
                              Today
                            </button>

                            <button
                              onClick={applyCustomRange}
                              className="rounded-xl bg-violet-600 px-3 py-2 text-[11px] font-semibold text-white shadow-sm shadow-violet-200 hover:bg-violet-700"
                              type="button"
                            >
                              Apply Custom
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2 text-[10px] text-slate-600">
                        <span className="font-semibold text-slate-800">
                          Applied:
                        </span>{" "}
                        <span className="font-semibold">{rangeLabel}</span>
                      </div>
                    </div>
                  </div>

                  <div className="sticky bottom-0 z-10 border-t border-slate-100 bg-white/95 px-4 py-3 backdrop-blur">
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <button
                        onClick={() => {
                          setSelectedBranchId("ALL");
                          resetToToday();
                          setRangeKey("TODAY");
                          setRangeError("");
                        }}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-[11px] font-semibold text-slate-700 hover:border-rose-200 hover:text-rose-700 sm:w-auto"
                        type="button"
                      >
                        Reset filters
                      </button>

                      <button
                        onClick={closeFilter}
                        className="w-full rounded-xl bg-slate-900 px-4 py-2 text-[11px] font-semibold text-white shadow-lg shadow-slate-900/30 hover:bg-slate-800 sm:w-auto"
                        type="button"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* If API hard-fails and no cached data, show a clean empty state */}
        {isError && !dashRes && (
          <Card className="p-5 mb-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Dashboard data not available
                </p>
                <p className="mt-1 text-[12px] text-slate-600">
                  {String(
                    error?.data?.message || error?.error || "Unknown error"
                  )}
                </p>
              </div>
              <button
                onClick={refetch}
                className="rounded-2xl bg-slate-900 px-4 py-2 text-[12px] font-semibold text-white shadow-lg shadow-slate-900/25 hover:bg-slate-800"
                type="button"
              >
                Retry
              </button>
            </div>
          </Card>
        )}

        

        {/* QUICK SHORTCUTS */}
        <motion.div variants={fadeUp(0.03)}>
          {isInitialLoading ? (
            <ShortcutsSkeleton />
          ) : (
            <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {quickShortcuts.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.to}
                  className="group relative overflow-hidden rounded-2xl bg-white/90 p-3 shadow-sm shadow-slate-200/70 ring-1 ring-slate-200/70 hover:ring-violet-300 hover:shadow-md transition-all"
                >
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-50 via-transparent to-violet-50/70 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex items-start gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-violet-50 text-lg ring-1 ring-violet-100">
                      <span>{item.emoji}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-500">
                        {item.tag}
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-[11px] text-slate-500 truncate">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                  <div className="relative mt-2 flex items-center justify-between text-[10px] text-slate-400">
                    <span className="inline-flex items-center gap-1">
                      <span className="h-1 w-1 rounded-full bg-emerald-500" />
                      <span>Shortcut</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-violet-500 group-hover:text-violet-700">
                      <span>Open</span>
                      <span>↗</span>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.div>

        {/* KPI STRIP */}
        <motion.div variants={fadeUp(0.05)}>
          {isInitialLoading ? (
            <KpiSkeleton />
          ) : (
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card className="p-4 ring-violet-100">
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-slate-500">
                    Gross Sales
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-emerald-600">
                    {shortInr(apiToday?.grossSales || 0)}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Invoices: {Number(apiToday?.invoiceCount || 0)}
                  </p>
                </div>
              </Card>

              <Card className="p-4 ring-amber-100">
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-slate-500">
                    Sales Return
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-amber-500">
                    {shortInr(apiToday?.salesReturn || 0)}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Returns: {Number(apiToday?.returnCount || 0)}
                  </p>
                </div>
              </Card>

              <Card className="p-4 ring-sky-100">
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-slate-500">
                    Net Sales
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-sky-600">
                    {shortInr(apiToday?.netSales || 0)}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Avg Bill: {shortInr(apiToday?.avgBill || 0)}
                  </p>
                </div>
              </Card>

              <Card className="p-4 ring-rose-100">
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-slate-500">
                    Bills Snapshot
                  </p>
                  <p className="mt-2 text-[15px] font-semibold text-slate-900">
                    {Number(apiToday?.invoiceCount || 0)} invoices •{" "}
                    {Number(apiToday?.returnCount || 0)} returns
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Range: {apiRange?.tz || "Asia/Kolkata"}
                  </p>
                </div>
              </Card>
            </div>
          )}
        </motion.div>

        {/* MAIN CHART ROW */}
        <motion.div
          variants={fadeUp(0.1)}
          className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr,0.9fr]"
        >
          {/* Monthly sales trend */}
          <Card className="p-4 ring-violet-100">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-900">
                  Monthly sales trend
                </h2>
                <p className="text-[11px] text-slate-500">
                  {hasCategoryBreakdown
                    ? "Category-wise monthly totals (stacked) + Net line (₹)"
                    : "Gold vs diamond + Net (₹)"}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {trendInsights.best && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Best: {trendInsights.best.month} •{" "}
                    {shortInr(trendInsights.best.netTotal || 0)}
                  </span>
                )}

                {/* ✅ YEAR SELECT */}
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="h-8 rounded-xl border border-slate-200 bg-white px-2 text-[11px]
                             hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100"
                >
                  {yearOptions.map((y) => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </select>

                {/* ✅ CATEGORY SELECT */}
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="h-8 rounded-xl border border-slate-200 bg-white px-2 text-[11px]
                             hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100"
                >
                  <option value="">All categories</option>
                  {categories.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>

                {/* View chip */}
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-900/90 px-3 py-1 text-[10px] font-semibold text-white ring-1 ring-white/10">
                  <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
                  {hasCategoryBreakdown ? "Stacked Categories" : "Gold vs Diamond"}
                </span>
              </div>
            </div>

            {trendBusy ? (
              <ChartSkeleton h={280} />
            ) : (
              <>
                {/* Premium tiles */}
                <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-2xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200">
                    <div className="text-[10px] text-slate-500">
                      This month Gross
                    </div>
                    <div className="text-[12px] font-extrabold text-slate-900">
                      {shortInr(lastPoint?.grossTotal || 0)}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      MoM {pct(lastPoint?.grossTotal, prevPoint?.grossTotal)}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
                    <div className="text-[10px] text-amber-700">
                      This month Returns
                    </div>
                    <div className="text-[12px] font-extrabold text-amber-800">
                      {shortInr(lastPoint?.returnTotal || 0)}
                    </div>
                    <div className="text-[10px] text-amber-700">
                      MoM {pct(lastPoint?.returnTotal, prevPoint?.returnTotal)}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-violet-50 px-3 py-2 ring-1 ring-violet-200">
                    <div className="text-[10px] text-violet-600">
                      This month Net
                    </div>
                    <div className="text-[12px] font-extrabold text-violet-800">
                      {shortInr(lastPoint?.netTotal || 0)}
                    </div>
                    <div className="text-[10px] text-violet-600">
                      MoM {pct(lastPoint?.netTotal, prevPoint?.netTotal)}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white px-3 py-2 ring-1 ring-slate-200">
                    <div className="text-[10px] text-slate-500">Bills</div>
                    <div className="text-[12px] font-extrabold text-slate-900">
                      {Number(lastPoint?.invoiceCount || 0)} inv
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {Number(lastPoint?.returnCount || 0)} returns
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr,260px]">
                  {/* Chart */}
                  <div className="rounded-2xl bg-gradient-to-b from-slate-50 to-white px-1 pt-10 shadow-inner ring-1 ring-slate-100">
                    <div className="flex justify-center">
                      <div className="w-full max-w-[900px]">
                        <div className="h-[300px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                              data={chartData}
                              margin={{ top: 10, right: 18, left: 0, bottom: 0 }}
                            >
                              <defs>
                                <linearGradient id="gNet" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#a855f7" stopOpacity={0.20} />
                                  <stop offset="100%" stopColor="#a855f7" stopOpacity={0.02} />
                                </linearGradient>

                                {categoryNames.map((nm, idx) => {
                                  const c = CAT_COLORS[idx % CAT_COLORS.length];
                                  return (
                                    <linearGradient
                                      key={`grad-${nm}`}
                                      id={`gCat-${idx}`}
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop offset="0%" stopColor={c} stopOpacity={0.22} />
                                      <stop offset="100%" stopColor={c} stopOpacity={0.03} />
                                    </linearGradient>
                                  );
                                })}
                              </defs>

                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis
                                dataKey="month"
                                stroke="#64748b"
                                fontSize={11}
                                tickLine={false}
                              />
                              <YAxis
                                stroke="#64748b"
                                fontSize={11}
                                tickFormatter={(v) =>
                                  Number(v || 0).toLocaleString("en-IN")
                                }
                                tickLine={false}
                              />

                              <Tooltip content={<TrendTooltip />} />

                              <Legend
                                wrapperStyle={{ fontSize: 11 }}
                                formatter={(value) =>
                                  String(value).length > 14
                                    ? `${String(value).slice(0, 14)}…`
                                    : value
                                }
                              />

                              {hasCategoryBreakdown &&
                                categoryNames.map((nm, idx) => {
                                  const c = CAT_COLORS[idx % CAT_COLORS.length];
                                  return (
                                    <Area
                                      key={`area-${nm}`}
                                      type="monotone"
                                      dataKey={nm}
                                      name={nm}
                                      stackId="1"
                                      stroke={c}
                                      fill={`url(#gCat-${idx})`}
                                      strokeWidth={1.8}
                                      dot={false}
                                      activeDot={{ r: 4 }}
                                    />
                                  );
                                })}

                              {!hasCategoryBreakdown && (
                                <>
                                  <Area
                                    type="monotone"
                                    dataKey="gold"
                                    name="Gold"
                                    stroke="#6366f1"
                                    fill="rgba(99,102,241,0.12)"
                                    strokeWidth={2.2}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="diamond"
                                    name="Diamond"
                                    stroke="#22c55e"
                                    fill="rgba(34,197,94,0.10)"
                                    strokeWidth={2.2}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                  />
                                </>
                              )}

                              <Line
                                type="monotone"
                                dataKey="netTotal"
                                name="Net"
                                stroke="#a855f7"
                                strokeWidth={2.8}
                                dot={false}
                                activeDot={{ r: 5 }}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right panel */}
                  <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm px-3 py-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-semibold text-slate-900">
                        Top months (Net)
                      </p>
                      <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white">
                        Rank
                      </span>
                    </div>

                    <div className="mt-2 space-y-2">
                      {trendInsights.top3?.length ? (
                        trendInsights.top3.map((m, idx) => (
                          <div
                            key={`${m.month}-${idx}`}
                            className="rounded-2xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-semibold text-slate-800">
                                #{idx + 1} • {m.month}
                              </span>
                              <span className="text-[10px] font-semibold text-violet-700">
                                {shortInr(m.netTotal)}
                              </span>
                            </div>
                            <div className="mt-1 text-[10px] text-slate-500">
                              Gross {shortInr(m.grossTotal)} • Return{" "}
                              {shortInr(m.returnTotal)}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-[11px] text-slate-500">No data</div>
                      )}
                    </div>

                    <div className="mt-3 rounded-2xl bg-violet-50 px-3 py-2 ring-1 ring-violet-200">
                      <div className="text-[10px] font-semibold text-violet-700">
                        Best month highlight
                      </div>
                      <div className="mt-0.5 text-[12px] font-extrabold text-violet-900">
                        {trendInsights.best ? trendInsights.best.month : "—"}
                      </div>
                      <div className="text-[10px] text-violet-700">
                        Net {shortInr(trendInsights.best?.netTotal || 0)}
                      </div>

                      {hasCategoryBreakdown && bestMonthTopCats.length ? (
                        <div className="mt-2 rounded-2xl bg-white/70 px-2 py-2 ring-1 ring-violet-200">
                          <div className="text-[10px] font-semibold text-slate-800">
                            Top categories
                          </div>
                          <div className="mt-1 space-y-1">
                            {bestMonthTopCats.map((x) => (
                              <div
                                key={x.name}
                                className="flex items-center justify-between gap-3 text-[10px] text-slate-600"
                              >
                                <span className="truncate max-w-[120px]">
                                  {x.name}
                                </span>
                                <span className="font-semibold text-slate-900">
                                  {shortInr(x.value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <button
                      onClick={downloadTrendCsv}
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-3 py-2 text-[11px] font-semibold text-white shadow-lg shadow-slate-900/25 hover:bg-slate-800"
                      type="button"
                    >
                      <DownloadIcon className="h-4 w-4" />
                      Download Category CSV
                    </button>

                    <p className="mt-2 text-[10px] text-slate-400">
                      CSV includes dynamic columns from categoryMonthly.
                    </p>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* RIGHT COLUMN CARDS */}
          <div className="flex flex-col gap-4">
            {/* ✅ UPDATED: Product-wise sales chart */}
            <Card className="p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-slate-900">
                    Product-wise sales (Amount)
                  </h2>
                  <p className="text-[11px] text-slate-500">
                    Selected category:{" "}
                    <span className="font-semibold">{productChartTitle}</span>
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[8px] text-slate-500">
  <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 ring-1 ring-slate-200">
    <span className="text-[11px]">📅</span>
    <span className="whitespace-nowrap">{formattedDate}</span>
  </span>

  <span className="inline-flex max-w-auto items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 ring-1 ring-slate-200">
    <span className="text-[11px]">🗓️</span>
    <span className="truncate">{rangeLabel}</span>
  </span>
</div>

                </div>

                <select
                  value={productCategoryId}
                  onChange={(e) => setProductCategoryId(e.target.value)}
                  className="h-8 rounded-xl border border-slate-200 bg-white px-2 text-[11px]
                             hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100"
                >
                  {categories.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {isInitialLoading || productLoading ? (
                <Sk className="h-[220px] w-full rounded-2xl" />
              ) : (
                <div className="h-[220px] w-full">
                  {productMix.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={productMix} barSize={22}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="name"
                          stroke="#64748b"
                          fontSize={8}
                          tickLine={false}
                          interval={0}
                          angle={-15}
                          height={50}
                        />
                        <YAxis
                          stroke="#64748b"
                          fontSize={11}
                          tickLine={false}
                          tickFormatter={(v) =>
                            Number(v || 0).toLocaleString("en-IN")
                          }
                        />
                        <Tooltip
                          formatter={(v) =>
                            `₹ ${Number(v || 0).toLocaleString("en-IN")}`
                          }
                        />
                        <Bar dataKey="amount" fill="#a855f7" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full grid place-items-center text-[12px] text-slate-500">
                      No product sales found for this category in selected range.
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Weekly scheme collections (Demo) */}
            {/* ✅ Design-wise sales (Amount) */}
<Card className="p-4">
  <div className="mb-3 flex items-center justify-between gap-2">
    <div className="min-w-0">
      <h2 className="text-sm font-semibold text-slate-900">
        Design-wise sales (Amount)
      </h2>
      <p className="text-[11px] text-slate-500">
        {designChartTitle}
      </p>

      <div className="mt-1 flex flex-wrap items-center gap-2 text-[8px] text-slate-500">
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 ring-1 ring-slate-200">
          <span className="text-[11px]">🗓️</span>
          <span className="truncate">{rangeLabel}</span>
        </span>
      </div>
    </div>

    <div className="flex items-center gap-2">
      {/* Category */}
      <select
        value={designCategoryId}
        onChange={(e) => {
          setDesignCategoryId(e.target.value);
          setDesignProductId(""); // reset product when category changes
        }}
        className="h-8 rounded-xl border border-slate-200 bg-white px-2 text-[11px]
                   hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100"
      >
        {categories.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>

      {/* Product */}
      <select
        value={designProductId}
        onChange={(e) => setDesignProductId(e.target.value)}
        disabled={!designProducts.length}
        className="h-8 rounded-xl border border-slate-200 bg-white px-2 text-[11px]
                   hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100 disabled:opacity-60"
      >
        {!designProducts.length ? (
          <option value="">No products</option>
        ) : (
          designProducts.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))
        )}
      </select>
    </div>
  </div>

  {isInitialLoading || designLoading || designProdLoading ? (
    <Sk className="h-[160px] w-full rounded-2xl" />
  ) : (
    <div className="h-[160px] w-full">
      {designMix.length ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={designMix} barSize={18}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="name"
              stroke="#64748b"
              fontSize={8}
              tickLine={false}
              interval={0}
              angle={-15}
              height={50}
            />
            <YAxis
              stroke="#64748b"
              fontSize={11}
              tickLine={false}
              tickFormatter={(v) => Number(v || 0).toLocaleString("en-IN")}
            />
            <Tooltip
              formatter={(v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`}
            />
            <Bar dataKey="amount" fill="#f97316" radius={[7, 7, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-full grid place-items-center text-[12px] text-slate-500">
          No design sales found for this selection.
        </div>
      )}
    </div>
  )}
</Card>

          </div>
        </motion.div>

        {/* BOTTOM ROW */}
        <motion.div
          variants={fadeUp(0.15)}
          className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr,0.8fr]"
        >
          <div className="flex flex-col gap-4">
          <Card className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-900">
                  Top 5 outstanding
                </h2>
                <p className="mt-1 text-[11px] text-slate-500">
                  Highest pending invoice balances
                </p>
              </div>

              <Link
                to="/reports/cre-debt-report"
                className="shrink-0 rounded-xl bg-red-50 px-3 py-1.5 text-[11px] font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-100"
              >
                View outstanding list
              </Link>
            </div>

            <div className="mt-3 rounded-2xl bg-red-50 px-3 py-2 ring-1 ring-red-100">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] font-semibold text-red-700">
                  Total outstanding
                </span>
                <span className="text-sm font-extrabold text-red-700">
                  {shortInr(totalOutstanding)}
                </span>
              </div>
            </div>

            {outstandingFetching ? (
              <ListSkeleton rows={5} tone="slate" />
            ) : topOutstanding.length === 0 ? (
              <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2 text-[12px] text-slate-500 ring-1 ring-slate-200">
                No outstanding invoices found
              </div>
            ) : (
              <ul className="mt-3 space-y-2">
                {topOutstanding.map((inv) => {
                  const outstanding = Number(
                    inv?.totals?.outstanding || inv?.outstanding || 0
                  );
                  const customerName =
                    inv?.customerId?.customerName ||
                    inv?.customerName ||
                    "Customer";
                  const customerCode = inv?.customerId?.customerId;
                  const invDate = inv?.invDate
                    ? new Date(inv.invDate).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "-";

                  return (
                    <li
                      key={inv?._id || inv?.invCode}
                      className="flex items-center justify-between gap-3 rounded-2xl bg-white px-3 py-2 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-[1px] hover:shadow-md hover:ring-red-200"
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-semibold text-slate-900">
                            {inv?.invCode || "Invoice"}
                          </p>
                          <p className="truncate text-[11px] text-slate-600">
                            {customerCode ? `${customerCode} - ` : ""}
                            {customerName}
                          </p>
                          <p className="mt-0.5 text-[10px] text-slate-400">
                            {invDate}
                            {inv?.branchId?.branchName
                              ? ` - ${inv.branchId.branchName}`
                              : ""}
                          </p>
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className="text-[13px] font-bold text-red-600">
                          {shortInr(outstanding)}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          Pending
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-4">
            <h2 className="text-sm font-semibold text-slate-900">
              Recent activity
            </h2>
            <p className="mt-1 text-[11px] text-slate-500">
              Highlights from dashboard summary.
            </p>

            {isInitialLoading ? (
              <ListSkeleton rows={4} tone="slate" />
            ) : (
              <ul className="mt-3 space-y-2 text-[13px] text-slate-700">
                {todayActivity.map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 rounded-2xl bg-slate-50/70 px-3 py-2 ring-1 ring-slate-200/70"
                  >
                    <span className="mt-[6px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-violet-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}

            {isError && (
              <div className="mt-3 rounded-2xl bg-rose-50 px-3 py-2 text-[11px] text-rose-700 ring-1 ring-rose-200">
                Dashboard API failed:{" "}
                <span className="font-semibold">
                  {String(
                    error?.data?.message || error?.error || "Unknown error"
                  )}
                </span>
              </div>
            )}
          </Card>
          </div>

         <Card className="p-4">
  <h2 className="text-sm font-semibold text-slate-900">
    Pending follow-ups
  </h2>
  <p className="mt-1 text-[11px] text-slate-500">
    Quotations needing follow-up
  </p>

  {pendingFollowUps.length === 0 ? (
    <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2 text-[12px] text-slate-500 ring-1 ring-slate-200">
      No pending follow-ups 🎉
    </div>
  ) : (
  <ul className="mt-3 space-y-2">
  {pendingFollowUps.map((qtn) => (
    <li
      key={qtn._id}
      className="group flex items-center justify-between gap-3 rounded-2xl bg-white px-3 py-2 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-[1px] hover:shadow-md hover:ring-amber-300"
    >
      {/* LEFT */}
      <div className="flex min-w-0 items-start gap-3">
        {/* status dot */}
        <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" />

        <div className="min-w-0">
          {/* Quotation code */}
          <p className="truncate text-[13px] font-semibold text-slate-900">
            {qtn.qtnCode}
          </p>

          {/* customer */}
          <p className="truncate text-[11px] text-slate-600">
            {qtn.customerName || qtn.customerCode || "Customer"}
          </p>

          {/* date */}
          <p className="mt-0.5 text-[10px] text-slate-400">
            {new Date(qtn.quotationDate || qtn.qtnDate).toLocaleDateString(
              "en-IN",
              { day: "2-digit", month: "short", year: "numeric" }
            )}
          </p>
        </div>
      </div>

      {/* RIGHT */}
      <div className="flex flex-col items-end gap-1 text-right">
        {/* amount */}
        <p className="text-[13px] font-semibold text-slate-900">
          ₹ {qtn.totals?.grandTotal?.toLocaleString("en-IN")}
        </p>

        {/* status pill */}
        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200">
          Follow-up pending
        </span>
      </div>
    </li>
  ))}
</ul>

  )}

  {/* VIEW ALL */}
  {pendingFollowUps.length > 0 && (
    <Link
      to="/quotations/quotation-list"
      className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-3 py-2 text-[11px] font-semibold text-white shadow-lg shadow-slate-900/30 hover:bg-slate-800"
    >
      View all follow-ups →
    </Link>
  )}
   <div className="mt-4 rounded-2xl bg-slate-900 px-3 py-2.5 text-[11px] text-slate-100 shadow-lg shadow-slate-900/30">
              <p className="font-medium">Today totals</p>
              <p className="mt-0.5 text-slate-400">
                Gross {shortInr(apiToday?.grossSales || 0)} • Return{" "}
                {shortInr(apiToday?.salesReturn || 0)} • Net{" "}
                {shortInr(apiToday?.netSales || 0)}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px]">
                  🧾 Invoices: {Number(apiToday?.invoiceCount || 0)}
                </span>
                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px]">
                  ↩️ Returns: {Number(apiToday?.returnCount || 0)}
                </span>
                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px]">
                  💰 Avg Bill: {shortInr(apiToday?.avgBill || 0)}
                </span>
              </div>
            </div>
</Card>

         
        </motion.div>
      </motion.div>


       <Link to="/masters/daily-rate-list">
  {/* STATIC WRAPPER (never moves) */}
  <div
    className="
      fixed bottom-20 right-6 sm:bottom-10 sm:right-20
      z-[50] cursor-pointer select-none
    "
  >
    {/* FLOATING LAYER (only up-down) */}
    <motion.div
      animate={{ y: [0, -6, 0] }}
      transition={{
        duration: 2.2,
        repeat: Infinity,
        ease: "easeInOut",
      }}
      className="relative flex flex-col items-center"
    >
      {/* GLOW (opacity only, centered) */}
      <motion.div
        className="
          absolute inset-0 rounded-full
          bg-gradient-to-br from-amber-300 via-yellow-200 to-orange-300
          blur-2xl
        "
        animate={{ opacity: [0.35, 0.7, 0.35] }}
        transition={{ duration: 1.8, repeat: Infinity }}
      />

      {/* COIN (rotate only) */}
      <motion.div
        // animate={{ rotate: 360 }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear",
        }}
        whileHover={{ scale: 1.12 }}
        whileTap={{ scale: 0.96 }}
        className="
          relative grid place-items-center
          h-12 w-12
          sm:h-14 sm:w-14
          lg:h-16 lg:w-16
          rounded-full
          bg-gradient-to-br from-slate-900 via-slate-800 to-black
          ring-2 ring-amber-300
          shadow-2xl shadow-amber-300/45
        "
      >
        {/* live dot (fixed position) */}
        <span className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>

        <span className="text-xl sm:text-2xl lg:text-3xl">🪙</span>
      </motion.div>
    </motion.div>

    {/* LABEL (NO MOTION = STABLE LOOK) */}
    <div
      className="
        mt-1 flex items-center justify-center gap-1
        rounded-full
        bg-slate-900/90 backdrop-blur
        px-2.5 py-0.5
        text-[9px] sm:text-[10px]
        font-bold tracking-widest
        text-amber-300
        shadow-md shadow-slate-900/40
      "
    >
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      LIVE RATE
    </div>
  </div>
</Link>

    </div>
  );
}


